import * as Sentry from '@sentry/react';
import type { DriverState } from '@playcastdotio/CoreTypes';
import type { DeviceConfigManager } from './DeviceConfigManager';

const DIAG = '[BLUE-STREAM-12010:ShadowcamPresenceMonitor]';

/** Matches PlayJector driver_status keys for plugged Windows slots (ShadowCam00, ShadowCam100, …). */
const SHADOWCAM_DRIVER_KEY_PATTERN = /^ShadowCam\d+$/;

/**
 * Grace window before we report plugged shadowcams missing from enumeration to Sentry.
 *
 * PlayJector plug + Chromium devicechange can lag driverState by hundreds of ms; wait for
 * `refreshAfterEnumeration` before capturing a missing-device issue.
 */
export const LAUNCH_PRESENCE_SETTLE_MS = 5_000;

export type ShadowcamUnhealthyReason = 'shadowcam_unhealthy';

export type ShadowcamHealth = {
  healthy: boolean;
  reason: ShadowcamUnhealthyReason | null;
  enumeratedShadowcams: number;
  driverShadowcams: { name: string; present: boolean }[] | null;
};

export type ShadowcamHealthListener = (health: ShadowcamHealth) => void;

export interface ShadowcamHealthSource {
  getHealth(): ShadowcamHealth;
  onHealthChange(listener: ShadowcamHealthListener): () => void;
  setRequiredSessionCameras(cameraNames: readonly string[]): void;
}

type DriverStateLike = DriverState | Record<string, boolean> | undefined;
type GetDriverStateFn = () => DriverStateLike;

/** Plugged slots from PlayJector driver_status (`true` ShadowCamNN keys only). */
export function getPluggedShadowcamDriverKeys(driverState: DriverStateLike): string[] {
  if (!driverState) {
    return [];
  }
  const record = driverState as Record<string, boolean>;
  return Object.entries(record)
    .filter(([key, value]) => SHADOWCAM_DRIVER_KEY_PATTERN.test(key) && value === true)
    .map(([key]) => key)
    .sort();
}

/**
 * Single source of truth for shadowcam health (BLUE-STREAM-12010).
 * USB-style pool: idle (no plugged driver keys) is healthy; when plugged, every
 * driver-reported slot must appear in Chromium enumerateDevices() after settle.
 */
export class ShadowcamPresenceMonitor {
  private readonly listeners = new Set<ShadowcamHealthListener>();
  private lastNotifiedHealth: ShadowcamHealth | null = null;
  private hasReportedDriverStatusIssue = false;
  private launchSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private requiredSessionCameras = new Set<string>();

  constructor(
    private readonly deviceConfig: DeviceConfigManager,
    private readonly getDriverState: GetDriverStateFn,
    private readonly settleMs: number = LAUNCH_PRESENCE_SETTLE_MS,
  ) {}

  /**
   * Call once after MediaCoordinator's first enumeration.
   *
   * Emits an info breadcrumb with the boot snapshot. Defers unhealthy Sentry capture
   * by `settleMs` so plug/enumeration races do not produce spurious issues.
   */
  public reportLaunchPresence(): void {
    const health = this.computeHealth();

    Sentry.addBreadcrumb({
      category: 'BLUE-STREAM-12010',
      level: 'info',
      message: 'Shadowcam presence at launch',
      data: {
        enumeratedShadowcams: health.enumeratedShadowcams,
        driverShadowcams: health.driverShadowcams,
      },
    });

    this.cancelLaunchSettleTimer();
    if (!health.healthy) {
      Sentry.logger.warn(
        `${DIAG} Plugged shadowcams not visible at first enumerate — waiting ${this.settleMs}ms for devicechange to settle`,
      );
      this.launchSettleTimer = setTimeout(() => {
        this.launchSettleTimer = null;
        const settled = this.computeHealth();
        if (settled.healthy) return;
        Sentry.logger.error(`${DIAG} Plugged shadowcams still missing from enumeration after settle window`);
        Sentry.captureMessage('[BLUE-STREAM-12010] Plugged shadowcams missing from host enumeration', {
          level: 'error',
          tags: {
            shadowcam_diag: 'BLUE-STREAM-12010',
            shadowcam_stage: 'launch_presence',
          },
          fingerprint: ['shadowcam-missing-at-launch'],
          extra: {
            enumeratedShadowcams: settled.enumeratedShadowcams,
            driverShadowcams: settled.driverShadowcams,
          },
        });
      }, this.settleMs);
    }

    this.notifyHealth(health);
  }

  private cancelLaunchSettleTimer(): void {
    if (this.launchSettleTimer !== null) {
      clearTimeout(this.launchSettleTimer);
      this.launchSettleTimer = null;
    }
  }

  /** Idempotent: fires the missing-enumeration issue at most once per session. */
  public reportDriverStatus(): void {
    const driverShadowcams = this.collectDriverShadowcams();

    if (!this.hasReportedDriverStatusIssue && driverShadowcams && driverShadowcams.length > 0) {
      const missing = driverShadowcams.filter((d) => !d.present);
      if (missing.length > 0) {
        this.hasReportedDriverStatusIssue = true;
        Sentry.logger.error(`${DIAG} driverState reports plugged shadowcams missing from enumeration: ${missing.map(d => d.name).join(', ')}`);
        Sentry.captureMessage('[BLUE-STREAM-12010] Plugged shadowcams missing from enumeration', {
          level: 'error',
          tags: {
            shadowcam_diag: 'BLUE-STREAM-12010',
            shadowcam_stage: 'driver_status_missing_enum',
          },
          fingerprint: ['driver-status-plugged-missing-from-enum'],
          extra: { driverShadowcams, missingShadowcams: missing.map((d) => d.name) },
        });
      }
    }

    this.notifyHealth(this.computeHealth());
  }

  public refreshAfterEnumeration(): void {
    const health = this.computeHealth();
    if (health.healthy) {
      this.cancelLaunchSettleTimer();
    }
    this.notifyHealth(health);
  }

  public setRequiredSessionCameras(cameraNames: readonly string[]): void {
    this.requiredSessionCameras = new Set(cameraNames.filter((name) =>
      SHADOWCAM_DRIVER_KEY_PATTERN.test(name),
    ));
    this.refreshAfterEnumeration();
  }

  public getHealth(): ShadowcamHealth {
    return this.computeHealth();
  }

  public onHealthChange(listener: ShadowcamHealthListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public destroy(): void {
    this.cancelLaunchSettleTimer();
    this.listeners.clear();
  }

  private computeHealth(): ShadowcamHealth {
    const enumerated = this.deviceConfig.getShadowcams();
    const driverShadowcams = this.collectDriverShadowcams();
    const pluggedNames = getPluggedShadowcamDriverKeys(this.getDriverState());

    // Health gate: only cameras the DRIVER reports as plugged are hard
    // requirements. requiredSessionCameras are routing identifiers from the
    // instance allocator — they don't imply the hardware exists yet (the
    // Android path allocates ShadowCamNN names before the driver advertises
    // the slots, so gating on them would permanently block matchmakeability
    // until the driver state catches up).
    if (pluggedNames.length === 0) {
      return {
        healthy: true,
        reason: null,
        enumeratedShadowcams: enumerated.length,
        driverShadowcams,
      };
    }

    const enumeratedHealthy = pluggedNames.every((name) =>
      enumerated.some((device) => device.displayName.includes(name)),
    );

    return {
      healthy: enumeratedHealthy,
      reason: enumeratedHealthy ? null : 'shadowcam_unhealthy',
      enumeratedShadowcams: enumerated.length,
      driverShadowcams,
    };
  }

  private collectDriverShadowcams(): { name: string; present: boolean }[] | null {
    const drivers = this.getDriverState();
    if (!drivers || Object.keys(drivers).length === 0) {
      return null;
    }

    const pluggedNames = getPluggedShadowcamDriverKeys(drivers);
    if (pluggedNames.length === 0) {
      return [];
    }

    const enumerated = this.deviceConfig.getShadowcams();
    return pluggedNames.map((name) => ({
      name,
      present: enumerated.some((device) => device.displayName.includes(name)),
    }));
  }

  private notifyHealth(health: ShadowcamHealth): void {
    if (this.healthEquals(this.lastNotifiedHealth, health)) {
      return;
    }
    this.lastNotifiedHealth = health;
    for (const listener of this.listeners) {
      try {
        listener(health);
      } catch (err) {
        Sentry.logger.error(`${DIAG} health listener threw: ${err}`);
      }
    }
  }

  private healthEquals(a: ShadowcamHealth | null, b: ShadowcamHealth): boolean {
    if (!a) return false;
    return (
      a.healthy === b.healthy &&
      a.reason === b.reason &&
      a.enumeratedShadowcams === b.enumeratedShadowcams &&
      JSON.stringify(a.driverShadowcams) === JSON.stringify(b.driverShadowcams)
    );
  }
}
